// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { authUtils, AUTH_STORAGE_KEY } from "@/lib/auth";
import { authService } from "@/services/auth";
import type { UserInfo } from "@/types/auth.types";

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    authUtils.isAuthenticated()
  );
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // 获取用户信息
  const updateUser = async () => {
    console.log("开始获取用户信息，认证状态:", isAuthenticated);

    try {
      if (!authUtils.isAuthenticated()) {
        console.log("无有效令牌，跳过获取用户信息");
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      console.log("开始调用 getCurrentUser API");
      const userInfo = await authService.getCurrentUser();
      console.log("获取用户信息成功:", userInfo);

      if (userInfo) {
        setUser(userInfo);
        setIsAuthenticated(true);
        console.log("用户信息和认证状态已更新");
      } else {
        console.log("获取到的用户信息为空");
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error("获取用户信息失败:", error);
      // 记录更详细的错误信息
      if (error instanceof Error) {
        console.error("错误详情:", error.message);
        console.error("错误堆栈:", error.stack);
      }

      // 检查是否为认证错误
      const status = (error as any)?.status || (error as any)?.response?.status;
      console.log("错误状态码:", status);

      if (status === 401) {
        console.log("认证失败，执行登出");
        handleLogout();
      }

      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // 登录
  const handleLogin = async (username: string, password: string) => {
    setLoading(true);
    try {
      const tokens = await authService.login(username, password);
      authUtils.setTokens(tokens);

      // 先获取用户信息
      await updateUser();

      // 在用户信息获取成功后再设置认证状态
      // 注意：updateUser 内部已经设置了 isAuthenticated，这里实际上是多余的
      // 但为了逻辑清晰，还是保留
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error("Login error:", error);
      authUtils.clearTokens();
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 登出
  const handleLogout = async () => {
    setLoading(true);
    try {
      if (isAuthenticated) {
        await authService.logout();
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      authUtils.clearTokens();
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
    }
  };

  // 初始化：检查认证状态和获取用户信息
  useEffect(() => {
    const initializeAuth = async () => {
      if (isAuthenticated) {
        await updateUser();
      }
      setLoading(false);
    };

    console.log("获取用户信息2");
    initializeAuth();
  }, []);

  // 监听存储变化（处理多标签页同步）
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === AUTH_STORAGE_KEY) {
        const isAuth = authUtils.isAuthenticated();
        setIsAuthenticated(isAuth);
        if (!isAuth) {
          setUser(null);
        } else {
          updateUser();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Loading 状态下显示加载指示器
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login: handleLogin,
        logout: handleLogout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
